import type { ReactNode } from "react"
import { ButtonBase } from "@mui/material"
import type { Theme } from "@mui/material"
import { makeStyles } from "tss-react/mui"

export type SegmentedControlOption<T extends string> = Readonly<{
    value: T
    label: ReactNode
}>

export type SegmentedControlColor = "primary" | "purple" | "dark"

const ACTIVE_SEGMENT_COLORS: Record<SegmentedControlColor, (theme: Theme) => { fill: string; hoverFill: string }> = {
    primary: (theme) => ({ fill: theme.palette.primary.main, hoverFill: theme.palette.primary.dark }),
    purple: (theme) => ({ fill: theme.palette.tailwind.purple[600], hoverFill: theme.palette.tailwind.purple[700] }),
    dark: (theme) => ({ fill: theme.palette.tailwind.slate[800], hoverFill: theme.palette.tailwind.slate[900] }),
}

/** "medium" is the page-level control; "small" is the compact one used inside forms and cards. */
export type SegmentedControlSize = "medium" | "small"

type Props<T extends string> = Readonly<{
    options: ReadonlyArray<SegmentedControlOption<T>>
    value: T | undefined
    onChange: (value: T) => void
    leadingLabel?: string
    color?: SegmentedControlColor
    size?: SegmentedControlSize
    fullWidth?: boolean
    className?: string
}>

type StyleProps = Readonly<{
    color: SegmentedControlColor
    size: SegmentedControlSize
    fullWidth: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { color, size, fullWidth }) => {
    const activeColors = ACTIVE_SEGMENT_COLORS[color](theme)
    const isSmall = size === "small"

    return {
        container: {
            display: fullWidth ? "flex" : "inline-flex",
            alignItems: "stretch",
            boxSizing: "border-box",
            height: isSmall ? undefined : "3rem",
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${isSmall ? theme.palette.tailwind.gray[300] : theme.palette.tailwind.slate[300]}`,
            borderRadius: isSmall ? ".5rem" : ".75rem",
            overflow: "hidden",
        },
        segment: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flex: fullWidth ? 1 : "initial",
            padding: isSmall ? ".375rem 1rem" : ".75rem 1.25rem",
            fontFamily: "inherit",
            fontWeight: 600,
            fontSize: isSmall ? ".75rem" : ".875rem",
            color: isSmall ? theme.palette.tailwind.gray[600] : theme.palette.tailwind.slate[800],
            whiteSpace: "nowrap",
            // Doubled & beats the MuiButtonBase border reset, so the divider also shows on buttons.
            "&&:not(:first-of-type)": {
                borderLeft: `1px solid ${isSmall ? theme.palette.tailwind.gray[200] : theme.palette.tailwind.slate[300]}`,
            },
        },
        button: {
            transition: theme.transitions.create(["background-color", "color", "box-shadow"], {
                duration: theme.transitions.duration.short,
            }),
            "&:hover": {
                backgroundColor: theme.palette.action.hover,
            },
        },
        buttonActive: {
            backgroundColor: activeColors.fill,
            color: theme.palette.common.white,
            "&:hover": {
                backgroundColor: activeColors.hoverFill,
            },
        },
    }
})

export function StandardSegmentedControl<T extends string>({
    options,
    value,
    onChange,
    leadingLabel,
    color = "primary",
    size = "medium",
    fullWidth = false,
    className,
}: Props<T>) {
    const { classes, cx } = useStyles({ color, size, fullWidth })

    return (
        <div className={cx(classes.container, className)}>
            {!!leadingLabel && <span className={classes.segment}>{leadingLabel}</span>}
            {options.map((option) => {
                const isActive = option.value === value

                return (
                    <ButtonBase
                        key={option.value}
                        type="button"
                        aria-pressed={isActive}
                        className={cx(classes.segment, classes.button, isActive && classes.buttonActive)}
                        onClick={() => onChange(option.value)}
                    >
                        {option.label}
                    </ButtonBase>
                )
            })}
        </div>
    )
}
